import { Card, Col, Divider, Row, Select, Spin } from 'antd';
import React, { Dispatch, FC, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import OrganizationStatisticsChart from './organization-statistics-chart';
import {
  OrganizationStatisticsChartData,
  OrganizationStatisticsChartKey,
  OrganizationStatisticsQuery,
  OrganizationStatisticsState,
  OrganizationStatisticsStore,
} from 'src/stores/statistics-store';
import { container } from 'src/inversify.config';
import moment from 'moment';
import { RangeValue } from 'src/core/ui/date-components/utils';
import { useTranslation } from 'react-i18next';
import { getDateFormat } from 'src/common/utils';
import DatePicker from 'src/core/ui/date-components/date-picker';

const { RangePicker } = DatePicker;

const OrganizationStatisticsChartPage: FC<{
  queryState: OrganizationStatisticsState;
  setQueryState: Dispatch<SetStateAction<OrganizationStatisticsState>>;
}> = ({ queryState, setQueryState }) => {
  const statisticsStore = container.get(OrganizationStatisticsStore);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<OrganizationStatisticsChartData>();
  const [organizationList, setOrganizationList] = useState<{ id: string; name: string }[]>();
  const [selectedOrganization, setSelectedOrganization] = useState<string>();
  const { t } = useTranslation();

  const [displayValues, setDisplayValues] = useState(
    queryState.query.startDate !== undefined && queryState.query.endDate !== undefined
      ? [moment(queryState.query.startDate), moment(queryState.query.endDate)]
      : [null, null]
  );

  useEffect(() => {
    load(queryState.query);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (query: OrganizationStatisticsQuery) => {
    setIsLoading(true);
    const data = await statisticsStore.loadCharts(query, selectedOrganization);
    setOrganizationList(statisticsStore.getOrganizationList());
    setChartData(data);
    setIsLoading(false);
  };

  const ChartCol: FC<PropsWithChildren> = ({ children }) => (
    <Col xl={12} sm={24} style={{ height: '100%' }}>
      {children}
    </Col>
  );

  const onChangeDates = (filter: RangeValue<moment.Moment>) => {
    const startDate = filter ? filter[0] : null;
    const endDate = filter ? filter[1] : null;

    if (!startDate || !endDate) {
      const newQuery: OrganizationStatisticsQuery = {
        startDate: undefined,
        endDate: undefined,
        mostUsed: queryState.query.mostUsed ?? false,
        partnerTenantId: queryState.query.partnerTenantId,
      };
      load(newQuery);
      setQueryState({ query: newQuery, selectorLabel: undefined });
    } else {
      const customLabel = `${filter![0]!.format(getDateFormat())} - ${filter![1]!.format(getDateFormat())}`;
      startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.add(1, 'day');
      endDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.subtract(1, 'minute');

      const newQuery: OrganizationStatisticsQuery = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        mostUsed: queryState.query.mostUsed ?? false,
        partnerTenantId: queryState.query.partnerTenantId,
      };
      load(newQuery);
      setQueryState({ query: newQuery, selectorLabel: customLabel });
    }
    setDisplayValues(filter as any);
  };

  return (
    <Spin spinning={isLoading}>
      <Card style={{ height: '100%' }}>
        <RangePicker
          classNames={{
            popup: {
              root: 'responsive-range-picker',
            },
          }}
          format={getDateFormat()}
          value={displayValues as any}
          allowClear
          onChange={onChangeDates}
          style={{ margin: 20, position: 'relative', float: 'left' }}
        />
        <Select
          placeholder={t('SelectOrganization')}
          allowClear
          style={{ marginTop: 20, marginRight: 20, position: 'relative', float: 'left', minWidth: '220px' }}
          onChange={async (v) => {
            setSelectedOrganization(v);
            const newChartData = await statisticsStore.getChartData(queryState.query, v);
            setChartData(newChartData);
          }}
        >
          {organizationList?.map((v) => (
            <Select.Option key={v.id} value={v.id}>
              {v.name}
            </Select.Option>
          ))}
        </Select>
        <Divider />
        <Row>
          <ChartCol>
            <OrganizationStatisticsChart
              createdData={chartData?.createdOrganizations ?? []}
              updatedData={chartData?.updatedOrganizations ?? []}
              chartKey={OrganizationStatisticsChartKey.Organizations}
            />
          </ChartCol>
          <ChartCol>
            <OrganizationStatisticsChart
              createdData={chartData?.createdProjects ?? []}
              updatedData={chartData?.updatedProjects ?? []}
              chartKey={OrganizationStatisticsChartKey.Projects}
            />
          </ChartCol>
          <ChartCol>
            <OrganizationStatisticsChart
              createdData={chartData?.createdSurveys ?? []}
              updatedData={chartData?.updatedSurveys ?? []}
              chartKey={OrganizationStatisticsChartKey.Surveys}
            />
          </ChartCol>
          <ChartCol>
            <OrganizationStatisticsChart
              createdData={chartData?.createdUsers ?? []}
              updatedData={chartData?.updatedUsers ?? []}
              chartKey={OrganizationStatisticsChartKey.Users}
            />
          </ChartCol>
        </Row>
      </Card>
    </Spin>
  );
};

export default OrganizationStatisticsChartPage;
